import React from 'react';
import { useAccountStore } from '@components/Stores/accountStore';
import { HedgePosition, DerivedHedgePosition } from '../../sp-types/positions';
import {
  doughnutToMap,
  FinancialMaterialSkeleton,
  positionsToDoughnut,
} from './components/FinancialPositionsUtils';
import type { ExposureObj } from './components/FinancialPositionsUtils';

import { DashBoardDonutCard } from '../../components/UI-Elements/DashBoardDonutCard';
import { IDashboardDonutChartDataSet } from '../../components/UI-Elements/DashBoardDonutCard';
import { FutureOrdersSummaryLegend } from '../FutureOrder/FutureOrders/FutureOrdersSummeryGraphs/FutureOrdersSummeryGraphsCharts/FutureOrdersSummaryLegend';
import { FutureOrdersSummeryBarChart } from '../FutureOrder/FutureOrders/FutureOrdersSummeryGraphs/FutureOrdersSummeryGraphsCharts/FutureOrdersSummeryBarChart';
import { useMaterialsStore } from '../../components/Stores/MaterialsStore';
import { HedgesGrid } from './components/HedgesGrid';
import { useBaseCurrency } from '../../hooks/useBaseCurrency';
import { ExposureHorizonWindow } from '../../utils/exposureHorizon';
import { useFormulasPrices } from '../../hooks/useFormulasPrices';
import { groupByFormula, getQuantity, getMonthYear, getPeriodDate } from '../../utils/positionUtils';

type HedgePositionEntry = HedgePosition | DerivedHedgePosition;

interface Props {
  positions: HedgePositionEntry[];
  setMaterial: (_materialId: string) => void;
  isGeneralMode?: boolean;
  formulaId?: string;
  exposureWindow?: ExposureHorizonWindow;
}

// ─── Chart helpers that work directly on HedgePosition ───────

/** Per-formula donut: total value per formula */
const hedgesToDoughnut = (
  positions: HedgePositionEntry[],
  materialsMap: Record<string, any>,
  colorsMap: Record<string, string>,
  pricesMap: Record<string, number>,
): IDashboardDonutChartDataSet[] => {
  const byFormula = groupByFormula(positions);
  return Object.entries(byFormula).map(([code, group]) => {
    const totalValue = group.reduce((sum, p) => sum + getQuantity(p) * (pricesMap[code] || 0), 0);
    return {
      fieldName: materialsMap[code]?.name || code,
      value: totalValue,
      color: colorsMap[code] || '',
      ratio: 0,
    };
  });
};

/** Per-formula bar chart: monthly breakdown keyed by formula_code */
const hedgesToBarChart = (
  positions: HedgePositionEntry[],
  pricesMap: Record<string, number>,
): Record<string, ExposureObj> => {
  const result: Record<string, ExposureObj> = {};
  for (const p of positions) {
    const code = p.formula_code;
    const value = getQuantity(p) * (pricesMap[code] || 0);
    const my = getMonthYear(getPeriodDate(p));
    const dateKey = `${my.year}${my.month}`;
    if (!result[code]) result[code] = { exposureCalendar: {} };
    result[code].exposureCalendar[dateKey] = (result[code].exposureCalendar[dateKey] || 0) + value;
  }
  return result;
};

/** Single-series "Hedges" bar chart for formula-specific mode */
const hedgesToSingleSeries = (
  positions: HedgePositionEntry[],
  pricesMap: Record<string, number>,
  color: string,
): Record<string, ExposureObj> => {
  const exposureCalendar: Record<string, number> = {};
  for (const p of positions) {
    const value = getQuantity(p) * (pricesMap[p.formula_code] || 0);
    const my = getMonthYear(getPeriodDate(p));
    const dateKey = `${my.year}${my.month}`;
    exposureCalendar[dateKey] = (exposureCalendar[dateKey] || 0) + value;
  }
  return { Hedges: { exposureCalendar, color } };
};

// ─── Component ───────────────────────────────────────────────

export const FinancialPositionsGeneral: React.FC<Props> = ({ positions, setMaterial, isGeneralMode = true, formulaId, exposureWindow }) => {
  const { formulaColorDictionary: colorsMap } = useAccountStore();
  const { materialsMap } = useMaterialsStore();
  const baseCurrency = useBaseCurrency();
  
  // Use new API-backed spot prices (not legacy useCurrencyRates)
  const { data: formulasPricesData } = useFormulasPrices();
  const pricesMap: Record<string, number> = React.useMemo(() => {
    if (!formulasPricesData?.pricesMap) return {};
    return Object.fromEntries(
      Object.entries(formulasPricesData.pricesMap).map(([key, val]) => [key, val.price])
    );
  }, [formulasPricesData?.pricesMap]);

  const currentFormulaColor =
    !isGeneralMode && formulaId ? colorsMap[formulaId] || '#3b82f6' : '#3b82f6';

  // General mode: donut data (per formula)
  const donutData = React.useMemo(
    () => (isGeneralMode ? hedgesToDoughnut(positions, materialsMap, colorsMap, pricesMap) : []),
    [positions, materialsMap, colorsMap, pricesMap, isGeneralMode],
  );

  // General mode: bar chart data (per formula, monthly)
  const barData = React.useMemo(
    () => (isGeneralMode ? hedgesToBarChart(positions, pricesMap) : {}),
    [positions, pricesMap, isGeneralMode],
  );

  // Formula-specific mode: single "Hedges" series
  const hedgesAggregated = React.useMemo(
    () => (!isGeneralMode ? hedgesToSingleSeries(positions, pricesMap, currentFormulaColor) : {}),
    [positions, pricesMap, isGeneralMode, currentFormulaColor],
  );

  // Item names for bar chart legend
  const itemNames = React.useMemo(() => {
    if (isGeneralMode) {
      const byFormula = groupByFormula(positions);
      return Object.keys(byFormula).reduce(
        (acc, code) => ({ ...acc, [code]: { name: materialsMap[code]?.name || code } }),
        {} as Record<string, { name: string }>,
      );
    }
    return { Hedges: { name: 'Net hedges' } };
  }, [positions, materialsMap, isGeneralMode]);

  return positions ? (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col bg-white shadow-custom p-3 justify-between max-h-60">
        <h6 className="flex gap-2">
          {isGeneralMode
            ? 'Summary'
            : `Summary for ${materialsMap[formulaId || '']?.name || formulaId}`}
        </h6>
        <div className="flex flex-1 gap-4">
          {isGeneralMode ? (
            <>
              <div className="w-48 p-4">
                <DashBoardDonutCard secondaryTitle="Net value" dataSet={donutData} />
              </div>
              <div className="flex my-auto min-w-[14rem] overflow-y-scroll max-h-[12rem]">
                <FutureOrdersSummaryLegend
                  materialsDataAsDictionary={doughnutToMap(donutData)}
                  colorsMap={colorsMap}
                  itemNames={donutData.reduce((acc, { fieldName }) => ({ ...acc, [fieldName]: { name: fieldName } }), {})}
                />
              </div>
              <div className="flex-1">
                <FutureOrdersSummeryBarChart
                  materialsDataAsDictionary={barData}
                  colorsMap={colorsMap}
                  itemNames={itemNames}
                />
              </div>
            </>
          ) : (
            <>
              <div className="w-48 p-3">
                <DashBoardDonutCard
                  secondaryTitle="Net value"
                  dataSet={positionsToDoughnut(hedgesAggregated)}
                />
              </div>
              <div className="flex my-auto min-w-[14rem] overflow-y-scroll max-h-[12rem]">
                <FutureOrdersSummaryLegend
                  materialsDataAsDictionary={doughnutToMap(positionsToDoughnut(hedgesAggregated))}
                  colorsMap={{ Hedges: currentFormulaColor }}
                  itemNames={{ Hedges: { name: 'Net hedges' } }}
                />
              </div>
              <div className="flex flex-1 max-h-48">
                <FutureOrdersSummeryBarChart
                  materialsDataAsDictionary={hedgesAggregated}
                  colorsMap={{ Hedges: currentFormulaColor }}
                  itemNames={{ Hedges: { name: 'Net hedges' } }}
                />
              </div>
            </>
          )}
        </div>
      </div>
      <div className="flex flex-col flex-1 shadow-custom">
        <HedgesGrid
          positions={positions}
          formulaColor={isGeneralMode ? '#3b82f6' : colorsMap[formulaId || ''] || '#3b82f6'}
          baseCurrency={baseCurrency}
          isGeneralMode={isGeneralMode}
          formulaName={!isGeneralMode && formulaId ? materialsMap[formulaId]?.name || formulaId : undefined}
          formulaId={!isGeneralMode ? formulaId : undefined}
          exposureWindow={exposureWindow}
        />
      </div>
    </div>
  ) : (
    <FinancialMaterialSkeleton />
  );
};
